import React, { useEffect, useMemo, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ErrorMessage } from '../../../../../../base/components/Forms/ErrorMessage';
import { Input } from '../../../../../../base/components/Forms/Input';
import { PasswordInput } from '../../../../../../base/components/Forms/PasswordInput';
import { PhoneInput } from '../../../../../../base/components/Forms/PhoneInput';
import { TermsCheckbox } from '../../../../../../base/components/Forms/TermsCheckbox';
import { Icon } from '../../../../../../base/components/Icon';
import { useAppDispatch, useAppSelector, useRoutesWithLang } from '../../../../../../base/hooks';
import { getRefById, RefMap, scrollToError } from '../../../../../../base/utils';
import { __t } from '../../../../../../base/utils/t';
import { signUpActions } from '../../../redux/slices/signUpSlice';
import { AuthRoutes } from '../../../routes';
import { AuthRoutes as WorkerAuthRoutes } from '../../../../../../worker/units/authorization/routes';

import '../styles.scss';

export const RegistrationInfo = () => {
    const location = useLocation();
    const dispatch = useAppDispatch();
    const {
        fields: { email, password, name, phone, agreeTerms },
        errors,
        shouldScrollToError,
    } = useAppSelector((state) => state.recruiterAppSignUp);
    const { phoneInputConfig } = useAppSelector((state) => state.recruiterApp.config);
    const authRoutes = useRoutesWithLang<typeof AuthRoutes>(AuthRoutes);
    const workerAuthRoutes = useRoutesWithLang<typeof WorkerAuthRoutes>(WorkerAuthRoutes);

    const refMap: RefMap[] = [
        {
            id: 'email',
            ref: useRef<HTMLDivElement>(null),
        },
        {
            id: 'password',
            ref: useRef<HTMLDivElement>(null),
        },
        {
            id: 'phone',
            ref: useRef<HTMLDivElement>(null),
        },
        {
            id: 'contactName',
            ref: useRef<HTMLDivElement>(null),
        },
        {
            id: 'terms',
            ref: useRef<HTMLDivElement>(null),
        },
    ];

    useEffect(() => {
        if (errors && shouldScrollToError) {
            scrollToError(errors, refMap);
            dispatch(signUpActions.setShouldScrollToError(false));
        }
    }, [refMap, errors, shouldScrollToError]);

    const inputChangeHandler = (id: string, value: unknown) => {
        dispatch(signUpActions.setField({ id, value }));
    };

    const mergedPhoneNameErrors = useMemo(() => {
        let result: string[] = [];
        if (errors?.name) {
            result = [...result, ...errors.name];
        }
        if (errors?.phone) {
            result = [...result, ...errors.phone];
        }
        return result;
    }, [errors]);

    return (
        <div className='recruiter-signup__form-wrapper'>
            <div
                className='recruiter-signup__form-item scroll-margin-for-fixed-header'
                ref={getRefById('email', refMap)?.ref}
            >
                <p className='recruiter-signup__form-label'>{__t('Электронная почта')}</p>
                <Input
                    value={email}
                    type='email'
                    placeholder={__t('Введите адрес почты')}
                    onChange={(value: string) => inputChangeHandler('email', value)}
                    errors={errors?.email}
                    tabIndex={1}
                />
            </div>

            <div
                className='recruiter-signup__form-item scroll-margin-for-fixed-header'
                ref={getRefById('password', refMap)?.ref}
            >
                <p className='recruiter-signup__form-label'>{__t('Придумайте пароль')}</p>
                <PasswordInput
                    value={password}
                    placeholder={__t('Минимум 6 символов')}
                    onChange={(value: string) => inputChangeHandler('password', value)}
                    className='recruiter-signup__form-input'
                    errors={errors?.password}
                    tabIndex={2}
                />
            </div>

            <div
                className='recruiter-signup__form-item scroll-margin-for-fixed-header'
                ref={getRefById('phone', refMap)?.ref}
            >
                <p className='recruiter-signup__form-label'>{__t('Контактное лицо')}</p>
                <PhoneInput
                    value={phone}
                    onChange={(value: string) => inputChangeHandler('phone', value)}
                    removeBorderRadius='bottom'
                    errors={errors?.phone}
                    hideErrorMessage={true}
                    onlyCountries={phoneInputConfig.countries}
                    preferredCountries={phoneInputConfig.preferredCountries}
                    localization={phoneInputConfig.localization}
                    tabIndex={3}
                />
                <Input
                    value={name}
                    placeholder={__t('Введите имя')}
                    onChange={(value: string) => inputChangeHandler('name', value)}
                    removeBorderRadius='top'
                    errors={errors?.name}
                    hideErrorMessage={true}
                    tabIndex={5}
                />
                <ErrorMessage errors={mergedPhoneNameErrors} />
            </div>

            <div
                className='recruiter-signup__form-item scroll-margin-for-fixed-header recruiter-signup__terms'
                ref={getRefById('terms', refMap)?.ref}
            >
                <TermsCheckbox
                    id='agreeTerms'
                    checked={agreeTerms}
                    onChange={inputChangeHandler}
                    hasError={!!errors?.agreeTerms?.length}
                >
                    <>
                        {__t('Я принимаю')}&nbsp;
                        <a
                            href={`${window.GLOBALS?.crossSiteBaseUrl}/pages/default/view?page=terms-and-conditions`}
                            target='_blank'
                            rel='noreferrer'
                        >
                            {__t('правила и условия сервиса')}
                        </a>
                        &nbsp;{__t('и')}&nbsp;
                        <a
                            href={`${window.GLOBALS?.crossSiteBaseUrl}/pages/default/view?page=privacy-policy`}
                            target='_blank'
                            rel='noreferrer'
                        >
                            {__t('политику конфиденциальности')}
                        </a>
                    </>
                </TermsCheckbox>
                <ErrorMessage errors={errors?.agreeTerms} />
            </div>

            <p className='recruiter-signup__message'>
                {__t('Уже зарегистрированы?')}&nbsp;
                <Link to={authRoutes.Login} state={{ from: location }} className='recruiter-signup__form-link'>
                    {__t('Войдите')}
                </Link>
            </p>

            <a
                href={`${window.GLOBALS?.crossSiteBaseUrl}${workerAuthRoutes.Login}`}
                className='recruiter-signup__workers-link'
            >
                <Icon
                    icon='user-helmet-safety-solid'
                    color='highlight'
                    className='recruiter-signup__workers-link-icon'
                />
                {__t('Я ищу работу')}
            </a>
        </div>
    );
};
